export interface Dispute {
  id: string
  scheme: 'Visa' | 'MasterCard' | 'UnionPay' | 'PayPak' | 'RAAST'
  transactionId: string
  arn: string
  transactionDate: string
  disputeAmount: number
  transactionAmount: number
  reasonCode: string
  reasonDescription: string
  disputeType: 'Fraud' | 'Retrieval' | 'Refund'
  responseDeadline: string // ISO date string
  status:
    | 'Pending Merchant Response'
    | 'Merchant Accepted'
    | 'Merchant Responded'
    | 'Under Review'
    | 'Chargeback Initiated'
    | 'Closed'
  cardLast4: string
  terminalId: string
  authorizationCode: string
  merchantId: string
  disputeStage: string
  merchantResponse?: {
    type: 'accept' | 'deny'
    explanation?: string
    evidenceFiles?: string[]
    submittedAt?: string
  }
  createdAt: string
  updatedAt: string
}

const now = new Date()
const addHours = (hours: number) =>
  new Date(now.getTime() + hours * 60 * 60 * 1000).toISOString()
const subDays = (days: number) =>
  new Date(now.getTime() - days * 24 * 60 * 60 * 1000).toISOString()

let disputes: Dispute[] = [
  {
    id: 'DSP-2026-001',
    scheme: 'Visa',
    transactionId: 'TX-982374',
    arn: 'ARN12345678901234567890123',
    transactionDate: subDays(15),
    disputeAmount: 4500,
    transactionAmount: 4500,
    reasonCode: '10.4',
    reasonDescription: 'Other Fraud - Card Absent Environment',
    disputeType: 'Fraud',
    responseDeadline: addHours(12), // Urgent
    status: 'Pending Merchant Response',
    cardLast4: '4242',
    terminalId: 'T-8472',
    authorizationCode: 'AUTH123',
    merchantId: '050102100023650',
    disputeStage: 'First Chargeback',
    createdAt: subDays(5),
    updatedAt: subDays(5),
  },
  {
    id: 'DSP-2026-002',
    scheme: 'MasterCard',
    transactionId: 'TX-982375',
    arn: 'ARN98765432109876543210987',
    transactionDate: subDays(20),
    disputeAmount: 12500,
    transactionAmount: 12500,
    reasonCode: '4837',
    reasonDescription: 'No Cardholder Authorization',
    disputeType: 'Fraud',
    responseDeadline: subDays(1), // Expired
    status: 'Chargeback Initiated',
    cardLast4: '5555',
    terminalId: 'Web',
    authorizationCode: 'AUTH456',
    merchantId: '050102100023650',
    disputeStage: 'First Chargeback',
    createdAt: subDays(8),
    updatedAt: subDays(1),
  },
  {
    id: 'DSP-2026-003',
    scheme: 'UnionPay',
    transactionId: 'TX-982376',
    arn: 'ARN55555555555555555555555',
    transactionDate: subDays(10),
    disputeAmount: 3200,
    transactionAmount: 3200,
    reasonCode: '4534',
    reasonDescription: 'Multiple Processing',
    disputeType: 'Refund',
    responseDeadline: addHours(72),
    status: 'Pending Merchant Response',
    cardLast4: '6222',
    terminalId: 'T-9231',
    authorizationCode: 'AUTH789',
    merchantId: '050102100023650',
    disputeStage: 'Retrieval Request',
    createdAt: subDays(2),
    updatedAt: subDays(2),
  },
  {
    id: 'DSP-2026-004',
    scheme: 'PayPak',
    transactionId: 'TX-982377',
    arn: 'ARN11111111111111111111111',
    transactionDate: subDays(30),
    disputeAmount: 1500,
    transactionAmount: 1500,
    reasonCode: '75',
    reasonDescription: 'Cardholder does not recognize transaction',
    disputeType: 'Retrieval',
    responseDeadline: addHours(120),
    status: 'Merchant Responded',
    cardLast4: '1234',
    terminalId: 'QR-01',
    authorizationCode: 'AUTH321',
    merchantId: '050102100023650',
    disputeStage: 'Retrieval Request',
    merchantResponse: {
      type: 'deny',
      explanation: 'Customer signed the receipt. See attached.',
      evidenceFiles: ['receipt.pdf'],
      submittedAt: subDays(1),
    },
    createdAt: subDays(6),
    updatedAt: subDays(1),
  },
  {
    id: 'DSP-2026-005',
    scheme: 'Visa',
    transactionId: 'TX-982378',
    arn: 'ARN22222222222222222222222',
    transactionDate: subDays(40),
    disputeAmount: 850,
    transactionAmount: 850,
    reasonCode: '13.1',
    reasonDescription: 'Merchandise/Services Not Received',
    disputeType: 'Refund',
    responseDeadline: addHours(48),
    status: 'Under Review',
    cardLast4: '4111',
    terminalId: 'T-8473',
    authorizationCode: 'AUTH654',
    merchantId: '050102100023650',
    disputeStage: 'Pre-Arbitration',
    merchantResponse: {
      type: 'deny',
      explanation: 'Tracking shows delivered.',
      evidenceFiles: ['tracking.png'],
      submittedAt: subDays(2),
    },
    createdAt: subDays(10),
    updatedAt: subDays(2),
  },
  {
    id: 'DSP-2026-006',
    scheme: 'MasterCard',
    transactionId: 'TX-982379',
    arn: 'ARN33333333333333333333333',
    transactionDate: subDays(50),
    disputeAmount: 5600,
    transactionAmount: 5600,
    reasonCode: '4853',
    reasonDescription: 'Cardholder Dispute',
    disputeType: 'Fraud',
    responseDeadline: subDays(10),
    status: 'Closed',
    cardLast4: '5100',
    terminalId: 'T-1045',
    authorizationCode: 'AUTH987',
    merchantId: '050102100023650',
    disputeStage: 'Arbitration',
    merchantResponse: {
      type: 'accept',
      submittedAt: subDays(12),
    },
    createdAt: subDays(20),
    updatedAt: subDays(10),
  },
  {
    id: 'DSP-2026-007',
    scheme: 'Visa',
    transactionId: 'TX-982380',
    arn: 'ARN44444444444444444444444',
    transactionDate: subDays(5),
    disputeAmount: 24000,
    transactionAmount: 24000,
    reasonCode: '10.3',
    reasonDescription: 'Other Fraud - Card Present Environment',
    disputeType: 'Fraud',
    responseDeadline: addHours(18), // Urgent
    status: 'Pending Merchant Response',
    cardLast4: '4000',
    terminalId: 'Web',
    authorizationCode: 'AUTH111',
    merchantId: '050102100023650',
    disputeStage: 'First Chargeback',
    createdAt: subDays(1),
    updatedAt: subDays(1),
  },
  {
    id: 'DSP-2026-008',
    scheme: 'UnionPay',
    transactionId: 'TX-982381',
    arn: 'ARN66666666666666666666666',
    transactionDate: subDays(25),
    disputeAmount: 7500,
    transactionAmount: 7500,
    reasonCode: '4536',
    reasonDescription: 'Late Presentment',
    disputeType: 'Retrieval',
    responseDeadline: addHours(200),
    status: 'Merchant Accepted',
    cardLast4: '6221',
    terminalId: 'T-8472',
    authorizationCode: 'AUTH222',
    merchantId: '050102100023650',
    disputeStage: 'Retrieval Request',
    merchantResponse: {
      type: 'accept',
      submittedAt: subDays(1),
    },
    createdAt: subDays(4),
    updatedAt: subDays(1),
  },
]

export const getDisputes = (): Dispute[] => {
  return [...disputes]
}

export const getDisputeById = (id: string): Dispute | undefined => {
  return disputes.find((d) => d.id === id)
}

export const submitDisputeResponse = (
  id: string,
  response: Dispute['merchantResponse'],
): Dispute | undefined => {
  const index = disputes.findIndex((d) => d.id === id)
  if (index === -1) return undefined

  const status =
    response?.type === 'accept' ? 'Merchant Accepted' : 'Merchant Responded'

  disputes[index] = {
    ...disputes[index],
    status,
    merchantResponse: response,
    updatedAt: new Date().toISOString(),
  }

  return disputes[index]
}

export const getDisputeStats = () => {
  return {
    total: disputes.length,
    pending: disputes.filter((d) => d.status === 'Pending Merchant Response')
      .length,
    responded: disputes.filter(
      (d) =>
        d.status === 'Merchant Responded' || d.status === 'Merchant Accepted',
    ).length,
    underReview: disputes.filter((d) => d.status === 'Under Review').length,
    chargebackInitiated: disputes.filter(
      (d) => d.status === 'Chargeback Initiated',
    ).length,
    closed: disputes.filter((d) => d.status === 'Closed').length,
  }
}

export const getSchemeLogoUrl = (scheme: string): string => {
  switch (scheme) {
    case 'Visa':
      return 'https://cdn.magicpatterns.com/uploads/3p73PbLehcANxLhkUCFJtP/Visa_Logo.png'
    case 'MasterCard':
      return 'https://cdn.magicpatterns.com/uploads/rAtf1iA9nLp74YVh82UyWQ/MasterCard_Logo.png'
    case 'UnionPay':
      return 'https://cdn.magicpatterns.com/uploads/dydeTq1f5i3QehVF2Vcyqo/UnionPay_Logo.png'
    case 'PayPak':
      return 'https://cdn.magicpatterns.com/uploads/ebrLJPCRwkf7jZMwJE1PsX/PayPak_Logo.png'
    case 'RAAST':
      return 'https://cdn.magicpatterns.com/uploads/aikHUBwr7qnGASTrGawPUh/RAAST_Logo.png'
    default:
      return ''
  }
}

export const getRemainingTime = (deadline: string) => {
  const now = new Date().getTime()
  const deadlineTime = new Date(deadline).getTime()
  const diff = deadlineTime - now

  if (diff <= 0) {
    return { days: 0, hours: 0, isExpired: true, isUrgent: false }
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))

  return {
    days,
    hours,
    isExpired: false,
    isUrgent: diff < 24 * 60 * 60 * 1000,
  }
}
